import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';

const ACTIVITY_THROTTLE_MS = 60_000;
const WARNING_SECONDS = 5 * 60;
const HUMAN_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
const BC_NAME = 'cavernicolapp-auth';

/**
 * S71 inactivity: human activity → throttled /auth/session/activity.
 * Warning modal at 55 min; logout at 60 min (server is source of truth).
 */
export default function SessionInactivityGuard() {
  const { user, logout, isImpersonating } = useAuth();
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const lastPing = useRef(0);
  const timerRef = useRef(null);

  const ping = useCallback(async (force = false) => {
    if (!user) return;
    const now = Date.now();
    if (!force && now - lastPing.current < ACTIVITY_THROTTLE_MS) return;
    lastPing.current = now;
    try {
      const { data } = await api.post('/auth/session/activity');
      setSecondsLeft(data.seconds_until_inactive ?? null);
      if ((data.seconds_until_inactive ?? 9999) <= WARNING_SECONDS) setShowWarning(true);
      else setShowWarning(false);
      try {
        localStorage.setItem('ca_session_activity_ts', String(Date.now()));
      } catch { /* ignore */ }
      try {
        const bc = new BroadcastChannel(BC_NAME);
        bc.postMessage({ type: 'activity', ts: Date.now() });
        bc.close();
      } catch { /* ignore */ }
    } catch (e) {
      if (e?.response?.status === 401) {
        await logout();
        window.location.href = '/login?reason=inactive';
      }
    }
  }, [user, logout]);

  useEffect(() => {
    if (!user) {
      setShowWarning(false);
      setSecondsLeft(null);
      return undefined;
    }
    const onHuman = () => { ping(false); };
    HUMAN_EVENTS.forEach((ev) => window.addEventListener(ev, onHuman, { passive: true }));
    // initial touch after login
    ping(true);
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s == null) return s;
        const next = s - 1;
        if (next <= WARNING_SECONDS) setShowWarning(true);
        if (next <= 0) {
          logout().finally(() => {
            window.location.href = '/login?reason=inactive';
          });
          return 0;
        }
        return next;
      });
    }, 1000);

    let bc;
    try {
      bc = new BroadcastChannel(BC_NAME);
      bc.onmessage = (ev) => {
        if (ev.data?.type === 'logout') {
          window.location.href = '/login?reason=logout';
        }
        if (ev.data?.type === 'activity') {
          setShowWarning(false);
          setSecondsLeft((s) => (s != null && s < WARNING_SECONDS + 60 ? s + 60 : s));
        }
      };
    } catch { /* ignore */ }

    return () => {
      HUMAN_EVENTS.forEach((ev) => window.removeEventListener(ev, onHuman));
      if (timerRef.current) clearInterval(timerRef.current);
      if (bc) bc.close();
    };
  }, [user, ping, logout]);

  if (!user || !showWarning || isImpersonating) return null;

  const mm = Math.floor(Math.max(0, secondsLeft || 0) / 60);
  const ss = Math.max(0, (secondsLeft || 0) % 60);

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4" data-testid="session-inactivity-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
        <h2 className="text-lg font-bold text-[#0F172A]">Tu sesión está por cerrarse</h2>
        <p className="text-sm text-[#64748B]">
          Por inactividad se cerrará en{' '}
          <span className="font-mono font-semibold text-[#0F172A]">
            {String(mm).padStart(2, '0')}:{String(ss).padStart(2, '0')}
          </span>
          . ¿Seguir conectado?
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => logout().then(() => { window.location.href = '/login'; })}>
            Cerrar sesión
          </Button>
          <Button
            className="bg-[#0F172A] text-white"
            data-testid="session-stay-logged-in"
            onClick={() => ping(true)}
          >
            Seguir conectado
          </Button>
        </div>
      </div>
    </div>
  );
}
